'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { flexRender } from '@tanstack/react-table';
import { AnimatePresence, LazyMotion, domMax } from 'motion/react';
import { forwardRef, } from 'react';
import { Carat, DoubleCarat, Filter, RotateClockwise, Search as SearchIcon, Triangle, } from '../Icons';
import { Menu } from '../Menu';
import { Skeleton } from '../Skeleton';
import { StyledCheckbox } from '../StyledCheckbox';
import { TypeStyle } from '../TypeStyle';
import * as Styles from './styles';
import { useDataTable } from './useDataTable';
function getColWidthStyle(column, fixedTotal, flexTotal) {
    const size = column.getSize();
    if (column.columnDef.meta?.fixedWidth || flexTotal === 0) {
        return { width: size };
    }
    return { width: `calc((100% - ${fixedTotal}px) * ${size / flexTotal})` };
}
const Root = forwardRef(function Root(props, ref) {
    return _jsx(Styles.Root, { ref: ref, ...props });
});
const ScrollContainer = forwardRef(function ScrollContainer(props, ref) {
    return _jsx(Styles.ScrollViewport, { ref: ref, ...props });
});
const ScrollArea = forwardRef(function ScrollArea({ dimmed, children, ...rest }, ref) {
    return (_jsxs(Styles.ScrollAreaRoot, { children: [_jsx(Styles.ScrollContainer, { ref: ref, ...rest, children: children }), dimmed && _jsx(Styles.LoadingOverlay, { "aria-hidden": true })] }));
});
function Table({ table, style, children, ...rest }) {
    const columns = table.getVisibleLeafColumns();
    const flexTotal = columns.reduce((total, column) => (column.columnDef.meta?.fixedWidth ? total : total + column.getSize()), 0);
    const fixedTotal = table.getTotalSize() - flexTotal;
    return (_jsxs(Styles.StyledTable, { style: { minWidth: table.getTotalSize(), ...style }, ...rest, children: [_jsx("colgroup", { children: columns.map(column => (_jsx("col", { style: getColWidthStyle(column, fixedTotal, flexTotal) }, column.id))) }), children] }));
}
const Header = forwardRef(function Header(props, ref) {
    return _jsx("thead", { ref: ref, ...props });
});
const HeaderRow = forwardRef(function HeaderRow(props, ref) {
    return _jsx("tr", { ref: ref, ...props });
});
const Body = forwardRef(function Body(props, ref) {
    return _jsx(Styles.Body, { ref: ref, ...props });
});
const Row = forwardRef(function Row({ selected, ...rest }, ref) {
    return _jsx(Styles.StyledRow, { ref: ref, "$selected": selected, "data-datatable-row": '', ...rest });
});
function Cell({ cell, style, ...rest }) {
    return (_jsx(Styles.StyledCell, { style: style, ...rest, children: flexRender(cell.column.columnDef.cell, cell.getContext()) }));
}
function HeaderCell({ header, style, ...rest }) {
    const canSort = header.column.getCanSort();
    const sorted = header.column.getIsSorted();
    const ariaSort = sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none';
    const content = header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext());
    if (!canSort) {
        return (_jsx(Styles.StyledHeaderCell, { scope: 'col', style: style, ...rest, children: content }));
    }
    return (_jsx(Styles.StyledHeaderCell, { scope: 'col', "aria-sort": ariaSort, style: style, ...rest, children: _jsxs(Styles.SortButton, { type: 'button', onClick: header.column.getToggleSortingHandler(), children: [content, _jsxs(Styles.SortIndicator, { "aria-hidden": true, children: [_jsx(Styles.SortArrow, { "$active": sorted === 'asc', children: _jsx(Triangle, { width: '13px', height: '10px' }) }), _jsx(Styles.SortArrow, { "$active": sorted === 'desc', children: _jsx(Triangle, { width: '13px', height: '10px', rotateClockwise: RotateClockwise.Half }) })] })] }) }));
}
function FloatingTableActions({ menu, content, show, ...rest }) {
    return (_jsx(LazyMotion, { features: domMax, children: _jsx(AnimatePresence, { children: show && (_jsx(Styles.FloatingTableActionsContainer, { initial: { opacity: 0, scale: 0.925, y: 8 }, animate: { opacity: 1, scale: 1, y: 0 }, exit: { opacity: 0, scale: 0.925, y: 8 }, transition: { duration: 0.125, ease: 'easeOut' }, ...rest, children: _jsxs(Styles.FloatingTableActionsCard, { children: [content && (_jsx(Styles.FloatingTableActionsCardContent, { children: content })), menu && (_jsx(Styles.FloatingTableActionsMenuContainer, { children: menu }))] }) })) }) }));
}
function FloatingTableActionsMenuButton({ children, ...rest }) {
    const menu = Menu.useState();
    const active = menu?.open ?? false;
    const caratPosition = active ? RotateClockwise.QuarterReverse : RotateClockwise.Quarter;
    return (_jsxs(Styles.FloatingTableActionsMenuButtonElement, { ...rest, children: [_jsx(Styles.FloatingTableActionsMenuButtonText, { children: children }), _jsx(Styles.FloatingTableActionsMenuButtonIcon, { children: _jsx(Carat, { "aria-hidden": true, rotateClockwise: caratPosition, transition: '0.3s' }) })] }));
}
function SelectionCheckbox({ checked, indeterminate, disabled, ariaLabel, onChange, }) {
    return (_jsxs(StyledCheckbox.Label, { disabled: disabled, children: [_jsx(StyledCheckbox.Input, { type: 'checkbox', checked: checked, indeterminate: indeterminate, disabled: disabled, "aria-label": ariaLabel, onChange: onChange }), _jsx(Styles.SelectionCheckboxElement, { checked: checked, indeterminate: indeterminate, disabled: disabled })] }));
}
function SelectionHeaderCell({ table }) {
    return (_jsx(Styles.SelectionCheckboxSlot, { children: _jsx(SelectionCheckbox, { ariaLabel: 'Select all rows', checked: table.getIsAllPageRowsSelected(), indeterminate: table.getIsSomePageRowsSelected(), onChange: table.getToggleAllPageRowsSelectedHandler() }) }));
}
function SelectionCell({ row }) {
    if (!row.getCanSelect()) {
        return (_jsx(Styles.SelectionCheckboxSlot, { children: _jsx(StyledCheckbox.Label, { "aria-hidden": true, children: _jsx(Styles.SelectionCheckboxElement, { checked: false, "$hidden": true }) }) }));
    }
    return (_jsx(Styles.SelectionCheckboxSlot, { children: _jsx(SelectionCheckbox, { ariaLabel: 'Select row', checked: row.getIsSelected(), onChange: row.getToggleSelectedHandler() }) }));
}
function Empty({ table, children = 'No results.', }) {
    return (_jsx("tr", { children: _jsx(Styles.StyledCell, { colSpan: table.getVisibleLeafColumns().length, children: _jsx(Styles.StateMessage, { children: children }) }) }));
}
function SkeletonRows({ table, rows = 5 }) {
    const columns = table.getVisibleLeafColumns();
    return (_jsx(_Fragment, { children: Array.from({ length: rows }).map((_, rowIndex) => (_jsx("tr", { children: columns.map(column => {
                const renderColumnSkeleton = column.columnDef.meta?.renderColumnSkeleton;
                return (_jsx(Styles.StyledCell, { children: renderColumnSkeleton ? (renderColumnSkeleton()) : (_jsx(Skeleton, { animated: true, height: '16px' })) }, column.id));
            }) }, rowIndex))) }));
}
function Toolbar({ search, children }) {
    return (_jsx(Styles.ToolbarWrapper, { children: _jsxs(Styles.ToolbarInner, { children: [search, children && _jsx(Styles.ToolbarActions, { children: children })] }) }));
}
function Search({ value, onChange, placeholder = 'Search', 'aria-label': ariaLabel = 'Search', adornment, ...rest }) {
    return (_jsxs(Styles.SearchFieldContainer, { ...rest, children: [_jsxs(Styles.SearchField, { role: 'search', children: [_jsx(Styles.SearchInput, { type: 'search', value: value, onChange: event => onChange(event.target.value), placeholder: placeholder, "aria-label": ariaLabel }), _jsx(Styles.SearchIconWrapper, { children: _jsx(SearchIcon, { height: '24px', width: '24px', "aria-hidden": true }) })] }), adornment && _jsx(Styles.SearchFieldAdornment, { children: adornment })] }));
}
const FilterButton = forwardRef(function FilterButton(props, ref) {
    return (_jsx(Styles.FilterButtonElement, { ref: ref, ...props, children: _jsx(Filter, { width: '20px', height: '20px', fill: 'white', stroke: 'currentColor' }) }));
});
function Pagination({ hasPreviousPage = false, hasNextPage = false, onFirstPage, onPreviousPage, onNextPage, onLastPage, firstLabel = 'First page', previousLabel = 'Previous page', nextLabel = 'Next page', lastLabel = 'Last page', pageIndicator, loading, children, ...rest }) {
    return (_jsxs(Styles.PaginationNav, { "aria-label": 'Pagination', ...rest, children: [_jsxs(Styles.PaginationControls, { children: [onFirstPage != null && (_jsx(Styles.PaginationButton, { type: 'button', "aria-label": firstLabel, disabled: !hasPreviousPage || loading, onClick: onFirstPage, children: _jsx(DoubleCarat, { "aria-hidden": true, rotateClockwise: RotateClockwise.Half, width: '10px', height: '10px' }) })), _jsx(Styles.PaginationButton, { type: 'button', "aria-label": previousLabel, disabled: !hasPreviousPage || loading, onClick: onPreviousPage, children: _jsx(Carat, { "aria-hidden": true, rotateClockwise: RotateClockwise.Half, width: '8px', height: '8px' }) }), _jsx(Styles.PaginationButton, { type: 'button', "aria-label": nextLabel, disabled: !hasNextPage || loading, onClick: onNextPage, children: _jsx(Carat, { "aria-hidden": true, width: '8px', height: '8px' }) }), onLastPage != null && (_jsx(Styles.PaginationButton, { type: 'button', "aria-label": lastLabel, disabled: !hasNextPage || loading, onClick: onLastPage, children: _jsx(DoubleCarat, { "aria-hidden": true, width: '10px', height: '10px' }) }))] }), (pageIndicator != null || children != null) && (_jsxs(Styles.PaginationInfo, { children: [pageIndicator != null && (_jsx(Styles.PaginationLabel, { children: pageIndicator })), children != null && _jsx(Styles.PaginationLabel, { children: children })] }))] }));
}
function Footer({ children }) {
    return _jsx(Styles.Footer, { "data-datatable-footer": true, children: children });
}
export function createSelectionColumn(opts) {
    const { fixedWidth = true, renderColumnSkeleton = () => (_jsx(Styles.SelectionCheckboxSlot, { children: _jsx(Skeleton, { width: '16px', height: '16px' }) })), ...baseOpts } = opts ?? {};
    return {
        id: 'select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        meta: {
            fixedWidth,
            renderColumnSkeleton,
        },
        header: ({ table }) => _jsx(SelectionHeaderCell, { table: table }),
        cell: ({ row }) => _jsx(SelectionCell, { row: row }),
        ...baseOpts,
    };
}
export function createTextColumn({ accessorKey, headerTitle, ...opts }) {
    const { fixedWidth, renderColumnSkeleton, ...baseOpts } = opts ?? {};
    return {
        accessorKey,
        header: () => (_jsx(TypeStyle.BodyNano, { fontWeight: 600, asTag: 'span', children: headerTitle })),
        cell: ({ getValue }) => (_jsx(TypeStyle.BodyNano, { fontWeight: 500, asTag: 'span', children: getValue() })),
        meta: {
            fixedWidth,
            renderColumnSkeleton,
        },
        ...baseOpts,
    };
}
export function createRendererColumn({ headerTitle, renderCell, ...opts }) {
    const { fixedWidth, renderColumnSkeleton, ...baseOpts } = opts ?? {};
    return {
        header: () => headerTitle ? (_jsx(TypeStyle.BodyNano, { fontWeight: 600, asTag: 'span', children: headerTitle })) : null,
        cell: ({ row }) => renderCell(row),
        meta: {
            fixedWidth,
            renderColumnSkeleton,
        },
        ...baseOpts,
    };
}
export const DataTable = {
    Root,
    Toolbar,
    Search,
    ScrollContainer,
    ScrollArea,
    Table,
    Header,
    HeaderRow,
    HeaderCell,
    Body,
    Row,
    Cell,
    Empty,
    SkeletonRows,
    Footer,
    Pagination,
    FilterButton,
    FiltersBar: Styles.FiltersBar,
    FloatingTableActions,
    FloatingTableActionsMenuButton,
};
export { useDataTable };
//# sourceMappingURL=index.js.map